import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useZoomStore } from '@/shared/store/zoomStore';
import { PRODUCT_NAME } from '@/shared/branding';
import { documentTitleFor } from './nav-config';

// On every page change: the tab title names the page, keyboard and
// screen-reader users land at the top of the new page (focus on <main>), and
// the window scrolls to the top (also on a browser Back: MemoryRouter keeps no
// scroll position per entry). Focus is left alone when the user or the page put
// it somewhere on purpose: inside <main> (a page focusing its first field), on a
// nav row (arrow-key travel keeps the tab focused), in the AI Companion (docked
// panel or floating drawer, [data-copilot]: its chat can navigate), in a dialog
// (a Modal), or while a zoom-out restore re-focuses (and scrolls to) a row.
export function RouteFocus(): null {
  const { pathname } = useLocation();
  const currentLabel = useZoomStore((s) => s.currentLabel);

  useEffect(() => {
    document.title = documentTitleFor(pathname, currentLabel);
  }, [pathname, currentLabel]);
  useEffect(() => () => { document.title = PRODUCT_NAME; }, []);

  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current === pathname) return; // first render, or StrictMode's second run
    lastPath.current = pathname;
    if (useZoomStore.getState().pendingFocus) return; // the row restore focuses and scrolls
    window.scrollTo(0, 0);
    const main = document.getElementById('main');
    if (!main) return;
    const active = document.activeElement as HTMLElement | null;
    if (active && active !== document.body
      && (main.contains(active) || active.closest('[data-mainnav],[data-subnav],[data-thirdnav],[data-copilot],[role="dialog"]'))) return;
    main.focus({ preventScroll: true });
  }, [pathname]);

  return null;
}
